import React, { useContext, useState } from 'react';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { useGlobalEvent } from 'beautiful-react-hooks';
import { useQueryClient } from 'react-query';

import FlashContext from '~/App/Components/Flash/Context';
import CustomDomainModal from '~/App/Modules/Settings/Domains/Modals/CustomDomainModal';
import { assetUrl } from '~/helpers';
import { useCancelAddOn } from '../../../Hooks/useAddOns';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { queryKeys as invoiceInfoKeys } from '../../../Hooks/useInvoiceInfo';
import { queryKeys as subscriptionKeys } from '../../../Hooks/useSubscription';
import { queryKeys as addOnKeys } from '../../../Hooks/useAddOns';
const trigger = 'cancel-custom-domain-add-on::trigger';

export interface CancelCustomDomainAddOnEventDetails {
  addOnUuid: string;
}

export const showCancelCustomDomainAddOnModal = ({
  addOnUuid,
}: CancelCustomDomainAddOnEventDetails) => {
  window.dispatchEvent(new CustomEvent(trigger, { detail: { addOnUuid } }));
};

const uid = 'cancel-custom-domain-add-on-modal';

const hideCancelCustomDomainAddOnModal = () => {
  window.dispatchEvent(new Event(`${uid}::close`));
};

const CancelCustomDomainAddOnModal = () => {
  const team = useCurrentTeam();
  const queryClient = useQueryClient();
  const { createFlash } = useContext(FlashContext);
  const [addOnUuid, setAddOnUuid] = useState<string>('');

  const onTriggerCancelCustomDomainAddOn = useGlobalEvent<
    CustomEvent<CancelCustomDomainAddOnEventDetails>
  >(trigger as keyof WindowEventMap);

  onTriggerCancelCustomDomainAddOn((event) => {
    setAddOnUuid(event.detail.addOnUuid);
    window.dispatchEvent(new Event(`${uid}::open`));
  });

  const { mutateAsync: cancelCustomDomainAddOn, isLoading: cancelling } =
    useCancelAddOn({
      teamId: team.id,
      addOnUuid,
      options: {
        onSuccess: () => {
          hideCancelCustomDomainAddOnModal();
          queryClient.invalidateQueries('app-config');
          queryClient.invalidateQueries(invoiceInfoKeys.detail);
          queryClient.invalidateQueries(addOnKeys.detail);
          queryClient.invalidateQueries(subscriptionKeys.lists());
          createFlash({
            id: 'cancel-custom-domain-success',
            title: 'Success',
            message:
              'Custom domain add-on cancellation processed successfully.',
          });
          window.location.reload();
        },
        onError: () => {
          createFlash({
            id: 'cancel-custom-domain-failed',
            title: 'Error',
            message:
              'Something went wrong while processing your request. Please try again.',
            options: { style: 'danger' },
          });
        },
      },
    });

  return (
    <CustomDomainModal uid={uid} className='!w-[438px]'>
      <div className='flex flex-col gap-y-7 items-center'>
        <img
          src={assetUrl('/assets/images/icons/add-ons/custom-domain.svg')}
          width='50'
          height='50'
        />
        <h4 className='text-xl v2-text-neutral-900 my-0 font-circular'>
          Cancel custom domain add-on
        </h4>
        <p className='text-sm v2-text-neutral-900 text-center leading-5 mb-0'>
          Are you sure you want to cancel your custom domain add-on? Your
          landing page URLs will default back to your previous settings.
        </p>
        <div className='flex flex-col gap-y-3 md:flex-row md:gap-x-4'>
          <Button
            className='w-full md:w-auto !py-2 h-[40px]'
            variant={ButtonVariant.Secondary}
            onClick={hideCancelCustomDomainAddOnModal}
            disabled={cancelling}
          >
            Cancel
          </Button>
          <Button
            className='w-full md:w-auto !py-2 h-[40px]'
            variant={ButtonVariant.Primary}
            onClick={() => void cancelCustomDomainAddOn()}
            disabled={cancelling}
          >
            {cancelling && <span className='fa fa-spinner spinning mr-2' />}
            Continue
          </Button>
        </div>
      </div>
    </CustomDomainModal>
  );
};

export default CancelCustomDomainAddOnModal;
